import { useSettings } from '@vmos2/providers';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useState } from 'react';

import {
  DiscountErrorReasons,
  UserPromotion as UserPromotionType,
  UserPromotions as UserPromotionsType,
} from '../../flow-types';
import { DiscountSource } from '../..';
import { UserPromotion, UserPromotionQRModal } from '.';

type Props = {
  currentApplyingPromotion?: UserPromotionType,
  disabled: boolean,
  discountedAmount?: number,
  errorReasons?: { [key: string]: DiscountErrorReasons } | DiscountErrorReasons,
  hasLoyaltyPointsDiscount: boolean,
  hasMultipleSelect: boolean,
  isApplyingDiscount: boolean,
  onChange?: (e: ChangeEvent, userPromotion: UserPromotionType) => void,
  promotionsBeingApplied: string[],
  selectedPromotions?: UserPromotionsType,
  showDescription: boolean,
  showQrCode: boolean,
  totalBundlesAmount?: number,
  userPromotions?: UserPromotionsType,
};

const DEFAULT_ERROR_REASONS = [];
const DEFAULT_PROMOTIONS_BEING_APPLIED = [];

export const UserPromotions = ({
  currentApplyingPromotion,
  disabled = false,
  discountedAmount,
  errorReasons = DEFAULT_ERROR_REASONS,
  hasLoyaltyPointsDiscount = false,
  hasMultipleSelect = false,
  isApplyingDiscount = false,
  onChange,
  promotionsBeingApplied = DEFAULT_PROMOTIONS_BEING_APPLIED,
  selectedPromotions,
  showDescription,
  showQrCode = false,
  totalBundlesAmount,
  userPromotions,
}: Props) => {
  const { acteolIntegration, discountsMultiFormat, loyaltyPosQR } = useSettings();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState();
  const isNewDiscountFormat = !!discountsMultiFormat?.online;

  const onClick = useCallback(userPromotion => {
    setSelectedPromotion(userPromotion);
    setIsModalOpen(true);
  }, []);

  if (!userPromotions?.length) {
    return null;
  }

  return (
    <>
      {userPromotions.map(userPromotion => {
        const { isActive = true, forceApply = false, providerPromotion, uuid: promotionUUID } = userPromotion;
        const rewardUUID = userPromotion.userReward?.uuid;
        const hasModal = showQrCode && !!acteolIntegration?.loyaltyEnabled && loyaltyPosQR && !!providerPromotion?.code;

        const appliedPromotion = selectedPromotions?.find(
          selectedPromotion =>
            (rewardUUID || selectedPromotion.userReward
              ? selectedPromotion.userReward?.uuid === rewardUUID
              : selectedPromotion.uuid === promotionUUID) && selectedPromotion.source !== DiscountSource.DISCOUNT_FORM
        );

        const isSelected = !!appliedPromotion;
        const isInactive = !!onChange && !isActive && !(isSelected && !userPromotion.forceDisabledOnInvalid);

        const isDisabled =
          isApplyingDiscount ||
          !!currentApplyingPromotion ||
          isInactive ||
          hasLoyaltyPointsDiscount ||
          disabled ||
          (hasMultipleSelect &&
            !selectedPromotions?.some(
              selectedPromotion =>
                selectedPromotion?.promoCode?.code && selectedPromotion?.source !== DiscountSource.DISCOUNT_FORM
            ) &&
            totalBundlesAmount <= 0 &&
            !isSelected) ||
          forceApply;

        const vmosPromotionUUID = providerPromotion?.vmosPromoUUID || promotionUUID;
        const applicableErrorReasons = isNewDiscountFormat ? errorReasons[vmosPromotionUUID] : errorReasons;

        const isLoading =
          (rewardUUID
            ? currentApplyingPromotion?.userReward?.uuid === rewardUUID
            : currentApplyingPromotion?.uuid === promotionUUID) ||
          promotionsBeingApplied.some(promotionBeingApplied =>
            rewardUUID ? promotionBeingApplied === rewardUUID : promotionBeingApplied === promotionUUID
          );

        const isMissingItems = isNewDiscountFormat
          ? appliedPromotion?.discount === 0
          : isEmpty(errorReasons) && discountedAmount === 0;

        return (
          <UserPromotion
            errorReasons={applicableErrorReasons}
            isDisabled={isDisabled}
            isInactive={isInactive}
            isInList={!onChange}
            isLoading={isLoading}
            isMissingItems={isMissingItems}
            isSelected={isSelected}
            key={`${userPromotion.uuid}${userPromotion.userReward?.uuid}`}
            onChange={onChange}
            showDescription={showDescription}
            userPromotion={userPromotion}
            {...(hasModal && { onClick })}
          />
        );
      })}

      <UserPromotionQRModal
        isOpen={isModalOpen}
        onAfterRequestClose={() => setSelectedPromotion(undefined)}
        onRequestClose={() => setIsModalOpen(false)}
        promotion={selectedPromotion}
      />
    </>
  );
};
